import { addMonths, differenceInCalendarDays } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import { forfeitDeposit, forfeitDepositForNoShow } from '@/domain/depositFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { recordContactAttempt } from '@/domain/sequenceFlow';
import { isShortNoticeBooking } from '@/domain/confirmationWorklist';
import { eventBus } from '@/events/eventBus';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { Booking, ConfirmationCall } from '@/domain/types';

/**
 * Confirmation outcomes (Prompt G, item 10). Every due confirmation resolves to
 * exactly one of these — nothing falls through silently.
 */
export type ConfirmationOutcome = 'confirmed' | 'rescheduled' | 'cancelled' | 'no-answer';

export interface ConfirmationOutcomeResult {
  ok: boolean;
  outcome?: ConfirmationOutcome;
  bookingId?: string;
  /** rescheduled: the UI must open the G1 reschedule flow (pick a new slot). */
  needsRescheduleUI?: boolean;
  /** cancelled with a paid deposit: the deposit that now sits in the money queue. */
  surfacedDepositId?: string;
}

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * A dateless / on-file deposit is valid 12 months from the instant it went on
 * file (vault Diagram 06, tv-bli). Deterministic 12-month add, no Date.now().
 */
export function onFileExpiryIso(fromIso: string): string {
  return addMonths(new Date(fromIso), 12).toISOString();
}

export function resolveConfirmationOutcome(
  callId: string,
  outcome: ConfirmationOutcome,
  opts?: { by?: string; at?: string },
): ConfirmationOutcomeResult {
  const brain = useBrainStore.getState();
  const call = brain.confirmationCalls[callId];
  if (!call) return { ok: false };
  const booking = brain.bookings[call.bookingId];
  if (!booking) return { ok: false };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'system';

  // Record the resolution on the call in every branch.
  brain.upsertConfirmationCall({ ...call, outcome });

  if (outcome === 'confirmed') {
    // A worklist confirm fully confirms the booking, not just a confirmedAt stamp:
    // flip the status so the waiver reconciler (which only walks confirmed
    // bookings) picks it up at T-4d, then fire the shared confirm hook so the
    // waiver + ICS appointment summary actually go out (tv-iui).
    brain.upsertBooking({ ...booking, status: 'confirmed', confirmedAt: at });
    onBookingConfirmed(booking.id);
    return { ok: true, outcome, bookingId: booking.id };
  }

  if (outcome === 'no-answer') {
    // Re-arm the existing 24h sequence window (reuse, do not fork) so the item
    // resurfaces as active work instead of disappearing.
    recordContactAttempt(booking.customerId);
    // Advance the per-booking cadence counter: drives which paired-day sequence
    // is next due. On the 3rd unanswered sequence, escalate to Ops exactly once -
    // stamp the flag the Ops worklist consumes and emit it (tv-k6t).
    const attempts = (booking.confirmationAttempts ?? 0) + 1;
    const escalate = attempts >= 3 && !booking.opsEscalatedAt;
    brain.upsertBooking({
      ...booking,
      confirmationAttempts: attempts,
      ...(escalate ? { opsEscalatedAt: at } : {}),
    });
    if (escalate) {
      eventBus.publish('confirmation:escalated', {
        bookingId: booking.id,
        customerId: booking.customerId,
        studioId: booking.studioId,
        at,
      });
    }
    return { ok: true, outcome, bookingId: booking.id };
  }

  if (outcome === 'cancelled') {
    const dep = booking.depositId ? brain.deposits[booking.depositId] : undefined;
    const hasPaidDeposit = !!dep && dep.status === 'paid' && depositPaidAud(dep) > 0;
    if (hasPaidDeposit) {
      // Split on the 7-day line relative to the booking start (vault Diagram 06,
      // tv-bli): whole calendar days from `at` to the appointment day.
      const daysUntilStart = differenceInCalendarDays(new Date(booking.start), new Date(at));
      if (daysUntilStart < 7) {
        // INSIDE 7 days: the deposit is forfeit to the house (deposit-loss). The
        // client must pay a fresh deposit to rebook. This is a cancel, not a
        // no-show, so the booking is set `cancelled` and never flipped to no-show.
        forfeitDeposit(booking.id, { reason: 'Cancelled inside 7-day window', by, at });
        brain.upsertBooking({ ...booking, status: 'cancelled' });
        return { ok: true, outcome, bookingId: booking.id };
      }
      // OUTSIDE 7 days: keep the deposit on file (existing G1 "no new date yet"
      // release), surfacing it to the money/Sweeper pool, then stamp the on-file
      // 12-month validity cap on the surfaced deposit.
      const res = rescheduleBooking({
        bookingId: booking.id,
        reason: 'client-move',
        pathway: { mode: 'release' },
        by,
        at,
      });
      if (res.surfacedDepositId) {
        // Re-read: applyReschedule unlinked the deposit, so the captured snapshot
        // above is stale.
        const surfaced = useBrainStore.getState().deposits[res.surfacedDepositId];
        if (surfaced) {
          brain.upsertDeposit({ ...surfaced, expiresAt: onFileExpiryIso(at) });
        }
      }
      return { ok: true, outcome, bookingId: booking.id, surfacedDepositId: res.surfacedDepositId };
    }
    // Nothing at stake — just release the slot.
    brain.upsertBooking({ ...booking, status: 'cancelled' });
    return { ok: true, outcome, bookingId: booking.id };
  }

  // rescheduled: the move itself is completed on the calendar (reason picker +
  // slot pick). Signal the UI to open the G1 flow.
  return { ok: true, outcome, needsRescheduleUI: true, bookingId: booking.id };
}

/**
 * Idempotent: every worklist row must open the work modal, including a chase
 * row that never had a confirmation touch scheduled (only overdue via the
 * day-of catch-up, not a stale call). Returns the existing ConfirmationCall id
 * for `bookingId` if one exists; otherwise mints a deterministic one - that
 * new record IS the touch about to be made - and returns its id. Re-opening
 * the same call-less row never creates a second call (stable id, upsert).
 * Returns '' (and persists nothing) when the booking does not exist - never
 * leave a junk call with no booking behind it.
 */
export function ensureConfirmationCall(
  bookingId: string,
  opts?: { at?: string; kind?: 'sms' | 'call' },
): string {
  const brain = useBrainStore.getState();
  const existing = Object.values(brain.confirmationCalls).find((c) => c.bookingId === bookingId);
  if (existing) return existing.id;

  const booking = brain.bookings[bookingId];
  if (!booking) return '';

  const id = `conf-adhoc-${bookingId}`;
  const call: ConfirmationCall = {
    id,
    bookingId,
    customerId: booking.customerId,
    scheduledFor: opts?.at ?? nowIso(),
    kind: opts?.kind ?? 'call',
    recommendedAction: 'human-preferred',
    projectedCostAud: 0,
  };
  brain.upsertConfirmationCall(call);
  return id;
}

/**
 * Live booking statuses that a short-notice auto-confirm can act on. A cancelled,
 * superseded, no-show or completed booking is never re-confirmed.
 */
const CONFIRMABLE_STATUSES = new Set<Booking['status']>(['held', 'pending-deposit', 'confirmed']);

/**
 * Short-notice / gap-fill auto-confirm at booking (vault Diagram 06, tv-bli). A
 * gap-fill client has just engaged, so the booking is confirmed at creation and
 * skips the pre-day cadence entirely. Confirms the booking (status + confirmedAt)
 * and fires the shared confirm hook (waiver + ICS) when the booking is live, not
 * already confirmed, and `isShortNoticeBooking`; otherwise a no-op returning
 * false (a normal-lead booking keeps its cadence). "Now" is the sim clock.
 *
 * Runtime gap-fill bookings are minted in the queue (Wave 0); wiring this into
 * that creation path is left to Wave 0 - this is the unit-tested hook.
 */
export function autoConfirmIfShortNotice(bookingId: string, opts?: { at?: string }): boolean {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return false;
  if (!CONFIRMABLE_STATUSES.has(booking.status)) return false; // not live
  if (booking.confirmedAt) return false; // already confirmed - do not double-confirm
  if (!isShortNoticeBooking(booking)) return false; // normal lead - keep the cadence
  const at = opts?.at ?? nowIso();
  brain.upsertBooking({ ...booking, status: 'confirmed', confirmedAt: at });
  onBookingConfirmed(bookingId);
  return true;
}

/**
 * Day-of no-show marking (Prompt G, item 10). Flips the booking to `no-show` and
 * triggers the G2 forfeit path on its paid deposit.
 */
export function markNoShow(
  bookingId: string,
  opts?: { by?: string; at?: string; note?: string },
): void {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return;
  const at = opts?.at ?? nowIso();
  brain.upsertBooking({ ...booking, status: 'no-show' });
  // L1 (17 Jul): the floor late-arrival forfeit forces a typed reason; thread it
  // onto the forfeit ledger note.
  forfeitDepositForNoShow(bookingId, { by: opts?.by ?? 'system', at, note: opts?.note });
}
